// catalog_manager read-only request detail — route
// /incoming-requests/:requestId (RequireRole(['catalog_manager']) only,
// App.jsx). Reads GET /catalog-manager/requests/:id: id/ref_no/needed_by
// plus each line's raw_description/raw_unit/qty and the matched product's
// id/name where one exists. No organization/customer identity anywhere, no
// quotation/pricing/status data, no assignment info, no notes, no document-
// access or audit history — this role's read never carries any of that
// (see backend/src/routes/catalogManagerRequestRoutes.js's own header
// comment).
//
// A brand new, deliberately narrow screen — NOT a reuse of
// AdminRequestDetailPage.jsx, which renders organization contact info,
// quotation history, notes, and an activity timeline this role must never
// see even behind a client-side gate (admin/CLAUDE.md: "never assume a
// hidden element is a security boundary" cuts both ways — don't build UI
// capable of showing data this role's own API was deliberately never given).

import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext.jsx';
import { useCatalogManagerRequestDetail } from '../hooks/useCatalogManagerRequests.js';
import LoadingState from '../components/LoadingState.jsx';
import ErrorState from '../components/ErrorState.jsx';
import EmptyState from '../components/EmptyState.jsx';
import { formatDate } from '../utils/formatDate.js';
import styles from './CatalogManagerRequestDetailPage.module.css';

export default function CatalogManagerRequestDetailPage() {
  const { requestId } = useParams();
  const { t, lang } = useLanguage();

  const detailQuery = useCatalogManagerRequestDetail(requestId);

  if (detailQuery.isLoading) {
    return <LoadingState variant="page" />;
  }
  if (detailQuery.isError) {
    return (
      <ErrorState
        messageId="catalogManagerRequestDetail.errorBody"
        error={detailQuery.error}
        onRetry={detailQuery.refetch}
      />
    );
  }

  const request = detailQuery.data;
  const lines = request.lines || [];

  return (
    <div className={styles.page}>
      <Link to="/incoming-requests" className={styles.backLink}>
        {t('catalogManagerRequestDetail.back')}
      </Link>

      <p className={styles.readOnlyHint}>{t('catalogManagerRequestDetail.readOnlyHint')}</p>

      <div className={styles.header}>
        <h1 className={`${styles.refNo} tabularNums`}>{request.ref_no}</h1>
        <div className={styles.metaRow}>
          <span className={styles.metaLabel}>
            {t('catalogManagerRequestDetail.header.neededBy')}
          </span>
          <span className="tabularNums">
            {request.needed_by
              ? formatDate(request.needed_by, lang)
              : t('catalogManagerRequestDetail.header.neededByNone')}
          </span>
        </div>
      </div>

      <hr className="hairline" />

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>{t('catalogManagerRequestDetail.lines.title')}</h2>

        {/* Zero lines on a real request is a normal state, not a failure
            (root CLAUDE.md's "zero matched Excel lines" framing). */}
        {lines.length === 0 ? (
          <EmptyState
            titleId="catalogManagerRequestDetail.lines.emptyTitle"
            bodyId="catalogManagerRequestDetail.lines.emptyBody"
          />
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                <th>{t('catalogManagerRequestDetail.lines.col.line')}</th>
                <th>{t('catalogManagerRequestDetail.lines.col.description')}</th>
                <th>{t('catalogManagerRequestDetail.lines.col.unit')}</th>
                <th>{t('catalogManagerRequestDetail.lines.col.qty')}</th>
                <th>{t('catalogManagerRequestDetail.lines.col.matched')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, i) => (
                <tr key={line.id}>
                  <td className="tabularNums">{i + 1}</td>
                  <td className={styles.lineDescription}>{line.raw_description}</td>
                  <td>{line.raw_unit || t('catalogManagerRequestDetail.lines.unitNone')}</td>
                  <td className="tabularNums">
                    {line.qty ?? t('catalogManagerRequestDetail.lines.qtyNone')}
                  </td>
                  <td>
                    {line.matched_product_id ? (
                      <span>{line.matched_product_name}</span>
                    ) : (
                      <span className={styles.noMatch}>
                        {t('catalogManagerRequestDetail.lines.noMatch')}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
